<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';

const minValue = defineModel<number>('min', { required: true });
const maxValue = defineModel<number>('max', { required: true });

const props = defineProps<{
  rangeMin: number;
  rangeMax: number;
  rangeStep: number;
  rangeDistance: number;
  labelMin?: string;
  labelMax?: string;
  dollarSymbol?: boolean;
  unlimitedLabel?: boolean;
  zeroLabel?: string;
}>();

const DEBOUNCE_MS = 1000;

const range = computed(() => {
  const RANGE_MIN = props.rangeMin ?? 0;
  const RANGE_MAX = props.rangeMax ?? 100;
  const RANGE_STEP = props.rangeStep ?? 1;
  const RANGE_DISTANCE = props.rangeDistance ?? 10;

  if (RANGE_MAX <= RANGE_MIN) {
    throw new Error('Range maximum must be greater than range minimum.');
  }

  if (RANGE_STEP <= 0) {
    throw new Error('Range step must be greater than 0.');
  }

  const steps = (RANGE_MAX - RANGE_MIN) / RANGE_STEP;

  if (steps !== Math.trunc(steps)) {
    throw new Error('Range step must evenly divide the range.');
  }

  if (steps < 1) {
    throw new Error('Range step must create at least 1 step.');
  }

  if (RANGE_DISTANCE < 0) {
    throw new Error('Range distance must be greater than or equal to 0.');
  }

  const distanceSteps = (steps * RANGE_STEP) / RANGE_DISTANCE;

  if (distanceSteps !== Math.trunc(distanceSteps)) {
    throw new Error('Range distance must be a multiple of range step.');
  }

  return {
    min: RANGE_MIN,
    max: RANGE_MAX,
    step: RANGE_STEP,
    distance: RANGE_DISTANCE,
  };
});

const i18n = useI18n();
const { t } = i18n;

const unlimitedString = computed(() => {
  return t('components.range.unlimited');
});

const formatNumber = (value: number) => {
  if (props.unlimitedLabel && value === range.value.max) {
    return unlimitedString.value;
  }
  if (props.zeroLabel && value === 0) {
    return props.zeroLabel;
  }
  const locale = i18n.locale.value;
  return value.toLocaleString(locale);
};

const clamp = (value: number, min: number, max: number) => {
  return Math.max(min, Math.min(value, max));
};

const roundToStep = (value: number) => {
  return Math.round(value / range.value.step) * range.value.step;
};

const cleanNumberInput = (input: string | undefined, defaultValue: number) => {
  if (input === undefined || input.length === 0) {
    return defaultValue;
  }
  const value = parseInt(input.replace(/\D/g, ''));
  if (isNaN(value)) {
    return defaultValue;
  }
  return value;
};

const initialMinValue = clamp(minValue.value, range.value.min, range.value.max - range.value.distance);
if (initialMinValue !== minValue.value) {
  minValue.value = initialMinValue;
}
const initialMaxValue = clamp(maxValue.value, range.value.min + range.value.distance, range.value.max);
if (initialMaxValue !== maxValue.value) {
  maxValue.value = initialMaxValue;
}

const minRangeValue = ref(initialMinValue);
const maxRangeValue = ref(initialMaxValue);

const minPercent = computed(() => ((minRangeValue.value - range.value.min) / (range.value.max - range.value.min)) * 100);
const maxPercent = computed(() => ((maxRangeValue.value - range.value.min) / (range.value.max - range.value.min)) * 100);

const minInput = ref('');
const maxInput = ref('');

const minPlaceholder = ref(formatNumber(initialMinValue));
const maxPlaceholder = ref(formatNumber(initialMaxValue));

const applyMinInput = () => {
  const val = roundToStep(cleanNumberInput(minInput.value, minValue.value));
  const clampedValue = clamp(val, range.value.min, maxValue.value - range.value.distance);
  minRangeValue.value = clampedValue;
  minPlaceholder.value = formatNumber(clampedValue);
  return clampedValue;
};

const applyMaxInput = () => {
  const val = roundToStep(cleanNumberInput(maxInput.value, maxValue.value));
  const clampedValue = clamp(val, minValue.value + range.value.distance, range.value.max);
  maxRangeValue.value = clampedValue;
  maxPlaceholder.value = formatNumber(clampedValue);
  return clampedValue;
};

const updateFromMinInput = () => {
  updateMinValue(applyMinInput());
  minInput.value = '';
};

const updateFromMaxInput = () => {
  updateMaxValue(applyMaxInput());
  maxInput.value = '';
};

// La valeur tapée s'applique toute seule après une pause : sans ça, il fallait cliquer
// ailleurs dans le panneau pour déclencher le blur avant que le filtre n'agisse.
const updateFromMinTyping = useDebounceFn(() => {
  if (minInput.value.length > 0) {
    minValue.value = applyMinInput();
  }
}, DEBOUNCE_MS);

const updateFromMaxTyping = useDebounceFn(() => {
  if (maxInput.value.length > 0) {
    maxValue.value = applyMaxInput();
  }
}, DEBOUNCE_MS);

watch(
  () => minInput.value,
  () => {
    if (minInput.value.length > 0) {
      void updateFromMinTyping();
    }
  }
);

watch(
  () => maxInput.value,
  () => {
    if (maxInput.value.length > 0) {
      void updateFromMaxTyping();
    }
  }
);

const updateFromMinSlider = () => {
  const clampedValue = clamp(minRangeValue.value, range.value.min, maxValue.value - range.value.distance);
  minRangeValue.value = clampedValue;
  updateMinValue(clampedValue);
  minPlaceholder.value = formatNumber(clampedValue);
  minInput.value = '';
};

const updateFromMaxSlider = () => {
  const clampedValue = clamp(maxRangeValue.value, minValue.value + range.value.distance, range.value.max);
  maxRangeValue.value = clampedValue;
  updateMaxValue(clampedValue);
  maxPlaceholder.value = formatNumber(clampedValue);
  maxInput.value = '';
};

const updateMinValue = useDebounceFn((value: number) => (minValue.value = value), DEBOUNCE_MS);
const updateMaxValue = useDebounceFn((value: number) => (maxValue.value = value), DEBOUNCE_MS);

watch(
  [range, minValue, maxValue],
  ([newRange, newMinValue, newMaxValue]) => {
    const newMin = clamp(newMinValue, newRange.min, newRange.max - newRange.distance);
    const newMax = clamp(newMaxValue, newRange.min + newRange.distance, newRange.max);

    minRangeValue.value = newMin;
    maxRangeValue.value = newMax;

    if (minValue.value !== newMin) minValue.value = newMin;
    if (maxValue.value !== newMax) maxValue.value = newMax;

    minPlaceholder.value = formatNumber(newMin);
    maxPlaceholder.value = formatNumber(newMax);
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <div>
      <!-- Editable Input Fields -->
      <div class="mb-4 flex items-center justify-between gap-3">
        <!-- Min input -->
        <div class="relative">
          <label v-if="labelMin" :for="'min-input'" class="pointer-events-none absolute -top-2 left-1.5 z-10 bg-white px-0.5 text-xs/3 font-medium text-gray-500">
            {{ labelMin }}
          </label>
          <div v-if="dollarSymbol" :class="['absolute top-1/2 -translate-y-1/2 font-semibold text-gray-500', i18n.locale.value === 'en-CA' ? 'left-1.5' : 'right-1.5']">$</div>
          <input
            id="min-input"
            v-model="minInput"
            :placeholder="minPlaceholder"
            @blur="updateFromMinInput"
            @keyup.enter="updateFromMinInput"
            inputmode="numeric"
            pattern="[0-9]*"
            type="text"
            :class="[
              'focus:outline-ph-green-600 w-32 rounded-md bg-white py-1 text-right font-medium text-gray-900 ring-1 ring-gray-300 -outline-offset-1 focus:outline-2 focus:-outline-offset-1',
              dollarSymbol ? (i18n.locale.value === 'en-CA' ? 'pr-2 pl-5' : 'pr-5 pl-2') : 'px-2',
            ]"
          />
        </div>
        <div class="text-gray-500">{{ t('components.range.to') }}</div>
        <!-- Max input -->
        <div class="relative">
          <label v-if="labelMax" :for="'max-input'" class="pointer-events-none absolute -top-2 left-1.5 z-10 bg-white px-0.5 text-xs/3 font-medium text-gray-500">
            {{ labelMax }}
          </label>
          <div v-if="dollarSymbol" :class="['absolute top-1/2 -translate-y-1/2 font-semibold text-gray-500', i18n.locale.value === 'en-CA' ? 'left-1.5' : 'right-1.5']">$</div>
          <input
            id="max-input"
            v-model="maxInput"
            :placeholder="maxPlaceholder"
            @blur="updateFromMaxInput"
            @keyup.enter="updateFromMaxInput"
            inputmode="numeric"
            pattern="[0-9]*"
            type="text"
            :class="[
              'focus:outline-ph-green-600 w-32 rounded-md bg-white py-1 text-right font-medium text-gray-900 ring-1 ring-gray-300 -outline-offset-1 focus:outline-2 focus:-outline-offset-1',
              dollarSymbol ? (i18n.locale.value === 'en-CA' ? 'pr-2 pl-5' : 'pr-5 pl-2') : 'px-2',
            ]"
          />
        </div>
      </div>

      <!-- Range Slider -->
      <div class="pb-1.5">
        <div class="group relative h-4">
          <div class="absolute top-1/2 z-0 h-1 w-full -translate-y-1/2 transform bg-gray-300"></div>
          <div class="bg-ph-green-700 absolute top-1/2 z-0 h-1 -translate-y-1/2 transform" :style="{ left: minPercent + '%', right: 100 - maxPercent + '%' }"></div>

          <!-- Range Inputs -->
          <div class="absolute inset-0 flex items-center">
            <input type="range" :min="range.min" :max="range.max" :step="range.step" v-model.number="minRangeValue" class="custom-range" @input="updateFromMinSlider" :key="`${range.min}-${range.max}-${range.step}-min`" tabindex="-1" />
          </div>
          <div class="absolute inset-0 flex items-center">
            <input type="range" :min="range.min" :max="range.max" :step="range.step" v-model.number="maxRangeValue" class="custom-range" @input="updateFromMaxSlider" :key="`${range.min}-${range.max}-${range.step}-max`" tabindex="-1" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="css"></style>
